import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import useConfirm from '../ui/useConfirm';
import './AdminUsuarios.css';

const API = '';

const FORM_VAZIO = {
  full_name: '',
  email: '',
  username: '',
  is_superuser: false,
};

export default function AdminUsuarios() {
  const { user } = useAuth();
  const [confirmEl, confirm] = useConfirm();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);

  // Modal novo usuário
  const [modalNovo, setModalNovo] = useState(false);
  const [form, setForm] = useState(FORM_VAZIO);
  const [salvando, setSalvando] = useState(false);
  const [erroForm, setErroForm] = useState(null);

  // Modal de link (convite ou reset) — tipo: 'invite' | 'reset'
  const [linkModal, setLinkModal] = useState(null); // { nome, link, tipo }
  const [mensagemCopiar, setMensagemCopiar] = useState('');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const carregarUsuarios = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const res = await fetch(`${API}/api/admin/usuarios`, { headers });
      if (!res.ok) throw new Error(`Erro ${res.status}`);
      setUsuarios(await res.json());
    } catch (e) {
      setErro(e.message);
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    carregarUsuarios();
  }, [carregarUsuarios]);

  const handleCriar = async (e) => {
    e.preventDefault();
    setSalvando(true);
    setErroForm(null);
    try {
      const res = await fetch(`${API}/api/admin/usuarios`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Erro ${res.status}`);
      setUsuarios((prev) => [...prev, data.user]);
      setModalNovo(false);
      setForm(FORM_VAZIO);
      setLinkModal({ nome: data.user.full_name || data.user.username, link: data.invite_link, tipo: 'invite' });
    } catch (e) {
      setErroForm(e.message);
    } finally {
      setSalvando(false);
    }
  };

  const handleGerarLinkReset = async (u) => {
    try {
      const res = await fetch(`${API}/api/admin/usuarios/${u.id}/reset-link`, {
        method: 'POST',
        headers,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Erro ${res.status}`);
      setLinkModal({ nome: u.full_name || u.username, link: data.link, tipo: 'reset' });
    } catch (e) {
      alert(`Erro ao gerar link: ${e.message}`);
    }
  };

  const handleCopiarLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      setMensagemCopiar('Link copiado!');
      setTimeout(() => setMensagemCopiar(''), 2000);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = link;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setMensagemCopiar('Link copiado!');
      setTimeout(() => setMensagemCopiar(''), 2000);
    }
  };

  const handleAlternarAtivo = async (u) => {
    const acao = u.is_active ? 'desativar' : 'ativar';
    if (!await confirm({
      title: `${acao === 'ativar' ? 'Ativar' : 'Desativar'} usuário`,
      message: `Deseja ${acao} o usuário "${u.username}"?`,
      confirmLabel: acao === 'ativar' ? 'Ativar' : 'Desativar',
      confirmVariant: acao === 'ativar' ? 'primary' : 'danger',
    })) return;
    try {
      const res = await fetch(`${API}/api/admin/usuarios/${u.id}/ativo`, {
        method: 'PUT',
        headers,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Erro ${res.status}`);
      setUsuarios((prev) => prev.map((x) => (x.id === data.id ? data : x)));
    } catch (e) {
      alert(`Erro: ${e.message}`);
    }
  };

  const fecharLinkModal = () => {
    setLinkModal(null);
    setMensagemCopiar('');
  };

  const fmtData = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('pt-BR');
  };

  return (
    <>
      {confirmEl}
      <h1 className="cw-page-title">Usuários</h1>
      <div className="admin-container">
      <div className="admin-header">
        <button className="btn-primary" onClick={() => { setModalNovo(true); setErroForm(null); setForm(FORM_VAZIO); }}>
          + Novo usuário
        </button>
      </div>

      {erro && <div className="admin-erro">{erro}</div>}

      {loading ? (
        <div className="admin-loading">Carregando...</div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Email</th>
                <th>Username</th>
                <th>Status</th>
                <th>Admin</th>
                <th>Criado em</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {usuarios.map((u) => (
                <tr key={u.id}>
                  <td>{u.full_name || '—'}</td>
                  <td>{u.email}</td>
                  <td>{u.username}</td>
                  <td>
                    <span className={`badge ${u.is_active ? 'badge-ativo' : 'badge-inativo'}`}>
                      {u.is_active ? 'Ativo' : 'Inativo'}
                    </span>
                    {!u.is_verified && (
                      <span className="badge badge-pendente" style={{ marginLeft: '4px' }}>
                        Pendente
                      </span>
                    )}
                  </td>
                  <td>{u.is_superuser ? 'Sim' : 'Não'}</td>
                  <td>{fmtData(u.created_at)}</td>
                  <td className="acoes">
                    <button
                      className="btn-sm btn-outline"
                      onClick={() => handleGerarLinkReset(u)}
                    >
                      Gerar link de reset
                    </button>
                    {u.username !== user?.username && (
                      <button
                        className={`btn-sm ${u.is_active ? 'btn-danger' : 'btn-success'}`}
                        onClick={() => handleAlternarAtivo(u)}
                      >
                        {u.is_active ? 'Desativar' : 'Ativar'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal novo usuário */}
      {modalNovo && (
        <div className="modal-overlay" onClick={() => setModalNovo(false)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <h3>Novo usuário</h3>
            <form onSubmit={handleCriar}>
              <label>
                Nome completo *
                <input
                  type="text"
                  required
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  placeholder="Nome completo"
                />
              </label>
              <label>
                Email *
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="email@exemplo.com"
                />
              </label>
              <label>
                Username *
                <input
                  type="text"
                  required
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  placeholder="username"
                />
              </label>
              <label className="label-checkbox">
                <input
                  type="checkbox"
                  checked={form.is_superuser}
                  onChange={(e) => setForm({ ...form, is_superuser: e.target.checked })}
                />
                Administrador
              </label>

              {erroForm && <div className="admin-erro">{erroForm}</div>}

              <div className="modal-actions">
                <button type="button" className="btn-outline" onClick={() => setModalNovo(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary" disabled={salvando}>
                  {salvando ? 'Criando...' : 'Criar e gerar convite'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de link (convite ou reset) */}
      {linkModal && (
        <div className="modal-overlay" onClick={fecharLinkModal}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <h3>
              {linkModal.tipo === 'invite' ? 'Convite gerado' : 'Link de reset de senha'}
            </h3>
            {linkModal.tipo === 'invite' ? (
              <p>
                Usuário criado. Repasse este link de convite para <strong>{linkModal.nome}</strong>:
              </p>
            ) : (
              <p>Link gerado para <strong>{linkModal.nome}</strong>:</p>
            )}
            <input
              type="text"
              readOnly
              value={linkModal.link}
              className="senha-destaque"
              style={{ cursor: 'text', fontSize: '0.8rem', wordBreak: 'break-all' }}
              onFocus={(e) => e.target.select()}
            />
            <p className="senha-aviso">
              {linkModal.tipo === 'invite'
                ? 'O link expira em 72 horas. Se expirar, gere um novo link de reset pelo botão na lista.'
                : 'Repasse este link ao usuário pelo canal interno (WhatsApp, Slack, etc). Ele expira em 60 minutos.'}
            </p>
            <div className="modal-actions">
              <button className="btn-outline" onClick={() => handleCopiarLink(linkModal.link)}>
                {mensagemCopiar || 'Copiar link'}
              </button>
              <button className="btn-primary" onClick={fecharLinkModal}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
}
